// src/pages/products/ProductCreatePage/ProductCreatePage.jsx
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useHistory, Link } from 'react-router-dom'
import ProductsAPI from '../../../api/v3/products-api-v3'
import AsyncLookup from '../../../components/form/AsyncLookup'
import ProductImagesAPI from '../../../api/v3/product-images-api-v3'

const BASE = '/app/sales/bagspa'

// env helper (CRA/Vite compatible)
const fromVite = (k) =>
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[k]) || undefined
const fromCRA = (k) =>
  (typeof process !== 'undefined' && process.env && process.env[k]) || undefined
const pickEnv = (viteKey, craKey) => fromVite(viteKey) ?? fromCRA(craKey)
const API_BASE = process.env.REACT_APP_API_BASE

// mapping from screenshot
const CATEGORY_CODES = {
  Bag: 'BG',
  Shoes: 'SH',
  Wallet: 'WL',
  Apparel: 'AP',
  Belt: 'OT',
  Others: 'OT',
}

function unwrapItems(payload) {
  if (!payload) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.items)) return payload.items
  if (Array.isArray(payload.data)) return payload.data
  return []
}

const r5 = () => String(Math.floor(Math.random() * 100000)).padStart(5, '0')
function buildSku(categoryName) {
  const code = CATEGORY_CODES[categoryName] || 'OT'
  return `BGS-${code}-${r5()}`
}

// ---- size helpers ----
function cmLabelFor(kind, min_cm, max_cm) {
  if (kind !== 'bag') return '' // only bags use numeric cm banding here
  if (min_cm && max_cm) return `${min_cm}-${max_cm} cm`
  if (min_cm && !max_cm) return `≥${min_cm} cm`
  if (!min_cm && max_cm) return `≤${max_cm} cm`
  return ''
}
function buildSizeOptionLabel(s) {
  const cm = cmLabelFor(s.kind, s.min_cm, s.max_cm)
  const bits = [
    cm || null,
    s.kind && s.kind !== 'generic' ? s.kind : null,
    s.dimension_text || null,
  ].filter(Boolean)
  // Example: "M · 26–29 cm • bag • Bag: dominant side 26–29 cm"
  const right = bits.length ? ` · ${bits.join(' • ')}` : ''
  return `${s.code || s.name || s.id}${right}`
}

export default function ProductCreatePage() {
  const history = useHistory()
  const fileInputRef = useRef(null)

  const [sku, setSku] = useState('')
  const [customer, setCustomer] = useState(null)
  const [brand, setBrand] = useState(null)
  const [category, setCategory] = useState(null)
  const [colour, setColour] = useState(null)
  const [size, setSize] = useState(null)
  const [material, setMaterial] = useState(null)

  // images
  const [files, setFiles] = useState([])
  const [coverIndex, setCoverIndex] = useState(-1)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const categoryName = useMemo(
    () => category?.name || category?.label || '',
    [category]
  )

  // 🔁 Always regenerate SKU whenever Category changes
  useEffect(() => {
    setSku(buildSku(categoryName || 'OT'))
  }, [categoryName])

  // --- async lookups -------------------------------------------------
  const fetchCustomers = useCallback(async (q) => {
    const url = new URL(`${API_BASE}/api/v3/customers`)
    if (q) url.searchParams.set('q', q)
    url.searchParams.set('per', '20')
    const res = await fetch(url.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    const json = await res.json()
    return unwrapItems(json).map((c) => ({
      id: c.id,
      label: `${c.name || '-'} · ${c.email || ''}`.trim(),
      name: c.name,
      email: c.email,
    }))
  }, [])

  const fetchSimpleList = useCallback(async (path, q) => {
    const url = new URL(`${API_BASE}${path}`)
    if (q) url.searchParams.set('q', q)
    url.searchParams.set('per', '20')
    const res = await fetch(url.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    const json = await res.json()
    return unwrapItems(json).map((x) => ({
      id: x.id,
      label: x.name || String(x.id),
      name: x.name,
    }))
  }, [])

  const fetchBrands = useCallback((q) => fetchSimpleList('/api/v3/brands', q), [fetchSimpleList])
  const fetchCategories = useCallback((q) => fetchSimpleList('/api/v3/categories', q), [fetchSimpleList])
  const fetchColours = useCallback((q) => fetchSimpleList('/api/v3/colours', q), [fetchSimpleList])
  const fetchMaterials = useCallback((q) => fetchSimpleList('/api/v3/materials', q), [fetchSimpleList])

  // ⬇️ Sizes: richer label with kind + cm + dimension_text
  const fetchSizes = useCallback(async (q) => {
    const url = new URL(`${API_BASE}/api/v3/sizes`)
    if (q) url.searchParams.set('q', q)
    url.searchParams.set('per', '20')

    const res = await fetch(url.toString(), {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })
    const json = await res.json()
    const items = unwrapItems(json)

    return items.map((s) => ({
      id: s.id,
      label: buildSizeOptionLabel(s),
      name: s.name,
      code: s.code,
      kind: s.kind,
      min_cm: s.min_cm,
      max_cm: s.max_cm,
      dimension_text: s.dimension_text,
    }))
  }, [])

  // --- image uploader -------------------------------------------------
  const onPick = () => fileInputRef.current?.click()
  const onFiles = (list) => {
    const arr = Array.from(list || []).filter((f) => /^image\//.test(f.type))
    if (arr.length) setFiles((prev) => [...prev, ...arr])
  }
  const onDrop = (e) => {
    e.preventDefault()
    onFiles(e.dataTransfer?.files)
  }
  const onDragOver = (e) => e.preventDefault()

  // --- submit form ----------------------------------------------------
  async function onSubmit(e) {
    e.preventDefault()
    setError('')

    if (!customer?.id) {
      setError('Please select a Customer before creating the product.')
      return
    }

    try {
      setSaving(true)
      const payload = {
        sku,
        customer_id: customer?.id || null,
        brand_id: brand?.id || null,
        category_id: category?.id || null,
        colour_id: colour?.id || null,
        size_id: size?.id || null,
        material_id: material?.id || null,
      }

      const created = await ProductsAPI.create(payload)
      const item = (created && (created.item || created.data || created)) || null
      const newId = item?.id
      if (!newId) throw new Error('Product created but no ID returned.')

      if (files.length) {
        setUploading(true)
        await ProductImagesAPI.bulkUpload(newId, files, coverIndex)
      }

      history.push(`${BASE}/products/${newId}`)
    } catch (e) {
      setError(e.message || 'Failed to create product')
    } finally {
      setSaving(false)
      setUploading(false)
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h2 className="mb-0">New Product</h2>
        <Link to={`${BASE}/products`} className="btn btn-link">
          &larr; Back to list
        </Link>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={onSubmit}>
        {/* SKU */}
        <div className="form-group">
          <label>SKU (auto-generated)</label>
          <input
            className="form-control"
            value={sku}
            disabled
            placeholder="Auto e.g. BGS-BG-01110"
          />
          <small className="form-text text-muted">
            Changes whenever <b>Category</b> changes. Format:{' '}
            <code>BGS-&#123;code&#125;-&#123;random5&#125;</code>.
          </small>
        </div>

        {/* Customer + Brand */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>
              Customer <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={customer}
              onChange={setCustomer}
              fetchFn={fetchCustomers}
              placeholder="Type customer name or email…"
            />
          </div>
          <div className="form-group col-md-6">
            <label>
              Brand (optional) <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={brand}
              onChange={setBrand}
              fetchFn={fetchBrands}
              placeholder="Type brand name…"
            />
          </div>
        </div>

        {/* Category + Colour */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>
              Category (optional) <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={category}
              onChange={setCategory}
              fetchFn={fetchCategories}
              placeholder="Type category…"
            />
          </div>
          <div className="form-group col-md-6">
            <label>
              Colour (optional) <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={colour}
              onChange={setColour}
              fetchFn={fetchColours}
              placeholder="Type colour…"
            />
          </div>
        </div>

        {/* Size + Material */}
        <div className="form-row">
          <div className="form-group col-md-6">
            <label>
              Size (optional) <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={size}
              onChange={setSize}
              fetchFn={fetchSizes}
              placeholder="Type size…"
            />
          </div>
          <div className="form-group col-md-6">
            <label>
              Material (optional) <small className="text-muted">(type to search)</small>
            </label>
            <AsyncLookup
              value={material}
              onChange={setMaterial}
              fetchFn={fetchMaterials}
              placeholder="Type material…"
            />
          </div>
        </div>

        <hr />

        {/* ---------------- BULK IMAGE UPLOAD ---------------- */}
        <h5 className="mb-2">Images (optional)</h5>
        <p className="text-muted">
          Drag & drop or click to select multiple images. Choose one as the main
          cover.
        </p>

        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          className="p-4 mb-3 border rounded text-center"
          style={{ background: '#fafafa', cursor: 'pointer' }}
          onClick={onPick}
        >
          <div className="mb-2">Drop image files here or click to browse</div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            className="d-none"
            onChange={(e) => onFiles(e.target.files)}
          />

          {files.length > 0 && (
            <div className="mt-3 small text-muted">
              {files.length} file(s) selected
              <div className="mt-2 d-flex flex-wrap gap-3 justify-content-center">
                {files.map((f, i) => (
                  <div
                    key={i}
                    className="p-2 border rounded text-center"
                    style={{ width: 120 }}
                  >
                    <img
                      src={URL.createObjectURL(f)}
                      alt=""
                      style={{
                        width: '100%',
                        height: 80,
                        objectFit: 'cover',
                        borderRadius: 6,
                      }}
                    />
                    <div className="form-check mt-1">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="cover"
                        checked={coverIndex === i}
                        onChange={() => setCoverIndex(i)}
                      />
                      <label className="small">Main</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <button className="btn btn-primary" type="submit" disabled={saving || uploading}>
          {saving ? 'Creating…' : uploading ? 'Uploading…' : 'Create Product'}
        </button>

        <div className="mt-2 text-muted small">
          Note: product requires at least a <b>Customer</b> per backend validation.
        </div>
      </form>
    </div>
  )
}
